// Intake detail. Read-only view of a completed intake (or in-progress,
// if the caller is the owner). Surfaces:
//   - Outcome card (severity, summary, message, contact, flags, next steps)
//   - Decision path + citations (the "why")
//   - Captured answers
//   - Generated drafts (when outcome.outcome === 'proceed')
//
// Owners get Resume / Revise / Delete actions. Non-owners only see the
// read-only view.

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Loader2,
  Edit3,
  Play,
  Trash2,
  MessageSquare,
} from 'lucide-react';
import { apiMaybe, apiRequest } from '../services/apiClient';
import {
  CapturedAnswersCard,
  GeneratedDraftsCard,
  OutcomeContactCard,
  OutcomeFlagsCard,
  OutcomeHeader,
  OutcomeNextStepsCard,
  OutcomeWhyCard,
} from '../features/generate/OutcomeCards';
import { AssistantTranscriptCard } from '../features/generate/TranscriptPanel';

export default function ScreeningDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [transcriptMessages, setTranscriptMessages] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiRequest(`/generate/sessions/${id}`)
      .then((body) => {
        setSession(body);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!session) {
      setTranscriptMessages([]);
      return;
    }
    apiMaybe(`/generate/sessions/${id}/messages`)
      .then((data) => setTranscriptMessages(data?.messages || []))
      .catch(() => setTranscriptMessages([]));
  }, [session, id]);

  // Pull contracts tied to this session so we can show generated
  // drafts. Runs for any *completed* session — non-screening flows
  // (NDA, etc.) never set a `proceed` outcome, but they do produce
  // contracts; gating on the outcome would hide them. The API just
  // returns nothing for sessions that didn't generate anything.
  useEffect(() => {
    if (!session || session.status !== 'completed') {
      setContracts([]);
      return;
    }
    apiMaybe(`/contracts?screening_session_id=${id}`)
      .then((data) => {
        const all = data?.contracts || data || [];
        const filtered = all.filter((c) => c.screening_session_id === id || c.screeningSessionId === id);
        setContracts(filtered.length ? filtered : all);
      })
      .catch(() => setContracts([]));
  }, [session, id]);

  async function destroy() {
    const linkedCount = contracts.length;
    const msg = linkedCount > 0
      ? `Delete this intake? This will also delete ${linkedCount} contract draft(s) generated from it. This cannot be undone.`
      : 'Delete this intake? This cannot be undone.';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await apiRequest(`/generate/sessions/${id}`, { method: 'DELETE' });
      navigate('/intake');
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  async function reviseFromHere() {
    if (!session) return;
    if (!window.confirm('Open this intake for revision? Any drafts already generated will be removed; new drafts will replace them when you finish.')) return;
    // Land in the wizard at the start; user can step through and edit any answer.
    navigate(`/intake/${session.typeKey}/${id}`);
  }

  if (loading) {
    return (
      <div className="canvas-inner">
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ margin: '0 auto 8px', color: 'var(--icon-secondary)' }} />
          <div className="muted" style={{ fontSize: 13 }}>Loading intake…</div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="canvas-inner">
        <div className="callout red">
          <div className="callout-body">
            <strong>Couldn't load intake.</strong>
            <div style={{ marginTop: 4, fontSize: 12 }}>{error || 'Not found.'}</div>
          </div>
        </div>
      </div>
    );
  }

  const outcome = session.outcome;
  const isOwn = session.isOwn !== false;
  // Owner OR an intake manager (legal/admin) — backs delete from the admin view.
  const canManage = session.canManage !== false;
  const isInProgress = session.status === 'in_progress';

  return (
    <div className="canvas-inner animate-fade-in" style={{ maxWidth: 820 }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Intake · {session.typeKey === 'grid' ? 'Grid Suite' : session.typeKey === 'nda' ? 'NDA' : session.typeKey}</div>
          <h1 className="page-title">
            {session.history?.[0]?.value || 'Untitled prospect'}
          </h1>
          <div className="page-subtitle">
            Intake ID: <span className="mono" style={{ fontSize: 12 }}>{id}</span>
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {isInProgress && isOwn && (
            <Link to={`/intake/${session.typeKey}/${id}`} className="btn primary">
              <Play className="w-4 h-4" />
              Resume
            </Link>
          )}
          {/* Once the intake produced contract drafts, lock revision —
              re-running render would silently delete those contracts
              and any work already done on them (file uploads, status
              changes, notes). The requester can start a fresh intake
              from /intake instead. */}
          {!isInProgress && isOwn && contracts.length === 0 && (
            <button type="button" className="btn outline" onClick={reviseFromHere} disabled={busy}>
              <Edit3 className="w-4 h-4" />
              Revise answers
            </button>
          )}
          {!isInProgress && isOwn && contracts.length > 0 && (
            <Link to="/intake" className="btn outline">
              <Play className="w-4 h-4" />
              Start new intake
            </Link>
          )}
          {canManage && (
            <button
              type="button"
              className="btn outline"
              onClick={destroy}
              disabled={busy}
              style={{ color: 'var(--text-critical)' }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* In-progress banner */}
      {isInProgress && (
        <div className="callout blue" style={{ marginBottom: 18 }}>
          <div className="callout-body" style={{ fontSize: 13 }}>
            This intake is still in progress. {isOwn ? 'Resume to continue from where you left off.' : 'Only the original author can resume.'}
          </div>
        </div>
      )}

      {(session.createdVia === 'slack' || session.createdVia === 'cli') && (
        <div className="callout blue" style={{ marginBottom: 18 }}>
          <MessageSquare className="w-4 h-4" style={{ color: 'var(--text-blue)', flexShrink: 0 }} />
          <div className="callout-body" style={{ fontSize: 13 }}>
            Created in {session.createdVia === 'slack' ? 'Slack' : 'the CLI'}. The assistant transcript below is stored for Legal review.
          </div>
        </div>
      )}

      {/* Outcome */}
      {outcome && (
        <div style={{ marginBottom: 18 }}>
          <OutcomeHeader outcome={outcome} />
          <OutcomeContactCard outcome={outcome} />
          <OutcomeWhyCard outcome={outcome} />
          <OutcomeFlagsCard outcome={outcome} />
          <OutcomeNextStepsCard outcome={outcome} />
        </div>
      )}

      {/* Generated drafts */}
      <GeneratedDraftsCard contracts={contracts} style={{ marginBottom: 18 }} />

      <AssistantTranscriptCard messages={transcriptMessages} />

      {/* Answers */}
      <CapturedAnswersCard history={session.history} />
    </div>
  );
}
